import type { CventActivitiesBaseProperties } from './types';
import type { Action, ActionProps } from '@cvent/analytics/Action';

/** Legacy (react-analytics `mapFact`) wire constants kept for the Attendee Activities consumer contract. */
const TYPE = 'aa_attendee_activities';
const DEFAULT_SCHEMA_VERSION = '1.0';

/**
 * Remove null/undefined properties — mirrors the legacy `cleanObject` in
 * `@cvent/react-analytics` `mapping/attendeeActivities.js`.
 */
function cleanObject(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(obj).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value != null) {
      acc[key] = value;
    }
    return acc;
  }, {});
}

/**
 * Build the Attendee Activities fact envelope in the shape the event-bus
 * consumer validates (`AttendeeActivitiesFactMessage`): `account_id`,
 * `environment`, `time`, `schema_version '1.0'` — the same shape the
 * known-good Attendee Hub publisher emits from the browser via
 * `@cvent/react-analytics` `mapping/attendeeActivities.js`.
 *
 * This intentionally differs from the `@cvent/analytics` AttendeeActivities
 * destination envelope (`env`, `ts` only, `account_mapping_id` only, `v1`),
 * which omits fields the consumer's `getMissingRequiredAttributes()` requires.
 */
export function buildAttendeeActivitiesEnvelope<A extends ActionProps>(
  action: Action<A>,
  baseProperties: CventActivitiesBaseProperties,
  timestamp: number = Date.now()
): Record<string, unknown> {
  const data = (action.data ?? {}) as Record<string, unknown>;

  return {
    type: TYPE,
    time: timestamp,
    ts: timestamp,
    environment: baseProperties.environment,
    activity_id: globalThis.crypto.randomUUID(),
    account_mapping_id: baseProperties.accountMappingId,
    account_id: baseProperties.accountMappingId,
    accountId: baseProperties.accountMappingId,
    attendee_type: baseProperties.attendeeType,
    attendee_id: baseProperties.attendeeId,
    activity_type: action.id,
    schema_version: DEFAULT_SCHEMA_VERSION,
    event_id: baseProperties.eventId,
    event_name: baseProperties.eventName,
    object_id: data.objectId,
    data: cleanObject({
      platform: baseProperties.platform,
      app_version: baseProperties.appVersion,
      ...data
    })
  };
}
